import React, { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import icon from 'leaflet/dist/images/marker-icon.png';
import shadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { Modal, Button, Input, Typography } from 'glintly-ui';
import { Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { useUpdateShop, cityService, cityQueryKeys } from '../../../api';
import { parseGoogleMapsUrl } from '../../../utils/parseGoogleMapsUrl';
import { searchPlacesPhoton, reverseGeocodePhoton } from '../../../utils/geocodeSearch';
import styles from './services.module.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: icon,
  shadowUrl: shadow,
});

const DEFAULT_CENTER = [19.076, 72.8777];

function findCityOptionId(cityName, cityOptions) {
  if (!cityName || typeof cityName !== 'string') return null;
  const n = cityName.trim().toLowerCase();
  if (!n) return null;
  const opt = cityOptions.find((o) => String(o.label).trim().toLowerCase() === n);
  return opt ? String(opt.value) : null;
}

function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapRecenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
    map.setView([lat, lng], Math.max(map.getZoom(), 15));
  }, [lat, lng, map]);
  return null;
}

/** Leaflet often mis-sizes inside a modal until invalidateSize runs */
function MapSizeFix() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

export default function ShopLocationModal({ isOpen, onClose, shop, cityOptions, onSaved }) {
  const [fullAddress, setFullAddress] = useState('');
  const [landmark, setLandmark] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('India');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [mapsLink, setMapsLink] = useState('');
  const [linkError, setLinkError] = useState('');
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [linkApplying, setLinkApplying] = useState(false);
  const [mapSearchError, setMapSearchError] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const queryClient = useQueryClient();
  const updateShopMutation = useUpdateShop();

  const resolveCityId = useCallback(
    async (cityName, latitude, longitude) => {
      const trimmed = typeof cityName === 'string' ? cityName.trim() : '';
      if (!trimmed) return '';
      const matched = findCityOptionId(trimmed, cityOptions);
      if (matched) return matched;
      const { city, created } = await cityService.ensureCity({
        cityName: trimmed,
        latitude:
          latitude != null && !Number.isNaN(Number(latitude)) ? Number(latitude) : undefined,
        longitude:
          longitude != null && !Number.isNaN(Number(longitude)) ? Number(longitude) : undefined,
      });
      queryClient.invalidateQueries({ queryKey: cityQueryKeys.all });
      if (created) {
        toast.success(`City "${city.cityName}" was added to your list.`);
      }
      return String(city._id);
    },
    [cityOptions, queryClient]
  );

  const applyPlaceFromGeocode = useCallback(
    async (place, opts = {}) => {
      if (!place) return;
      const { cityHintFromUrl, localityHintFromUrl } = opts;
      const la = place.lat;
      const ln = place.lng;
      setLat(String(Math.round(la * 1e6) / 1e6));
      setLng(String(Math.round(ln * 1e6) / 1e6));
      setLinkError('');
      setMapSearchError('');
      let addrLine =
        (place.fullAddressLine && place.fullAddressLine.trim()) ||
        (place.label && place.label.trim()) ||
        '';
      const loc = localityHintFromUrl && localityHintFromUrl.trim();
      if (loc) {
        if (addrLine && !addrLine.toLowerCase().includes(loc.toLowerCase())) {
          addrLine = `${loc}, ${addrLine}`;
        } else if (!addrLine) {
          addrLine = loc;
        }
      }
      if (addrLine) setFullAddress(addrLine);
      if (place.postcode) setPostalCode(place.postcode);
      if (place.country) setCountry(place.country);
      const cityFromPhoton =
        place.cityName && String(place.cityName).trim() ? String(place.cityName).trim() : '';
      const cityHint =
        cityHintFromUrl && String(cityHintFromUrl).trim() ? String(cityHintFromUrl).trim() : '';
      const cityToResolve = cityHint || cityFromPhoton;
      if (cityToResolve) {
        try {
          const id = await resolveCityId(cityToResolve, la, ln);
          if (id) setCity(id);
        } catch {
          toast.error('Could not match or create city. Choose city manually.');
        }
      }
    },
    [resolveCityId]
  );

  const latNum = lat === '' ? null : Number(lat);
  const lngNum = lng === '' ? null : Number(lng);
  const markerValid =
    latNum != null && lngNum != null && !Number.isNaN(latNum) && !Number.isNaN(lngNum);

  const center = markerValid ? [latNum, lngNum] : DEFAULT_CENTER;

  const resetFromShop = useCallback(() => {
    if (!shop?.address) {
      setFullAddress('');
      setLandmark('');
      setCity('');
      setPostalCode('');
      setCountry('India');
      setLat('');
      setLng('');
      setMapsLink('');
      setLinkError('');
      setMapSearchQuery('');
      setMapSearchError('');
      setSearchResults([]);
      return;
    }
    const a = shop.address;
    setFullAddress(a.fullAddress || a.street || '');
    setLandmark(a.landmark || '');
    setCity(
      typeof a.city === 'object' && a.city?._id ? String(a.city._id) : a.city ? String(a.city) : ''
    );
    setPostalCode(a.postalCode || '');
    setCountry(a.country || 'India');
    const la =
      a.latitude != null
        ? a.latitude
        : a.location?.coordinates?.[1] != null
          ? a.location.coordinates[1]
          : '';
    const ln =
      a.longitude != null
        ? a.longitude
        : a.location?.coordinates?.[0] != null
          ? a.location.coordinates[0]
          : '';
    setLat(la !== '' && la != null ? String(la) : '');
    setLng(ln !== '' && ln != null ? String(ln) : '');
    setMapsLink('');
    setLinkError('');
    setMapSearchQuery('');
    setMapSearchError('');
    setSearchResults([]);
  }, [shop]);

  useEffect(() => {
    if (isOpen && shop) resetFromShop();
  }, [isOpen, shop, resetFromShop]);

  const handlePick = async (latitude, longitude) => {
    const la = Math.round(latitude * 1e6) / 1e6;
    const ln = Math.round(longitude * 1e6) / 1e6;
    setLat(String(la));
    setLng(String(ln));
    setLinkError('');
    setMapSearchError('');
    try {
      const rev = await reverseGeocodePhoton(la, ln);
      if (rev) {
        await applyPlaceFromGeocode(rev);
        toast.success('Address details filled from map location.');
      } else {
        toast.info('Pin set. Add address details manually if needed.');
      }
    } catch {
      toast.error('Could not look up address for this point.');
    }
  };

  const runMapSearch = async () => {
    const q = mapSearchQuery.trim();
    if (!q) {
      toast.info('Type a place name or address to search.');
      return;
    }
    setMapSearchError('');
    setSearchResults([]);
    setMapSearchLoading(true);
    try {
      const bias =
        markerValid && latNum != null && lngNum != null
          ? { lat: latNum, lon: lngNum }
          : { lat: DEFAULT_CENTER[0], lon: DEFAULT_CENTER[1] };
      const results = await searchPlacesPhoton(q, bias);
      setSearchResults(results);
      if (results.length === 0) {
        setMapSearchError(
          'No places found. Try a shorter query, another spelling, or pick on the map.'
        );
      }
    } catch {
      setMapSearchError('Search failed. Check your connection and try again.');
    } finally {
      setMapSearchLoading(false);
    }
  };

  const pickSearchResult = async (r) => {
    setSearchResults([]);
    setMapSearchError('');
    setLinkError('');
    try {
      await applyPlaceFromGeocode(r);
      toast.success('Location applied from search.');
    } catch {
      toast.error('Could not apply this result.');
    }
  };

  const applyMapsLink = async () => {
    setLinkError('');
    const parsed = parseGoogleMapsUrl(mapsLink);
    if (!parsed) {
      setLinkError(
        'Could not read this link. Paste a URL with @lat,lng, ?q=address, /place/Name, or ?query=… Short goo.gl links often cannot be read in the browser—open the link, copy the long URL from the address bar, and paste that.'
      );
      return;
    }

    if (parsed.placeQuery) {
      const q = parsed.placeQuery.trim();
      setLinkApplying(true);
      try {
        const bias =
          markerValid && latNum != null && lngNum != null
            ? { lat: latNum, lon: lngNum }
            : { lat: DEFAULT_CENTER[0], lon: DEFAULT_CENTER[1] };
        const results = await searchPlacesPhoton(q, bias);
        if (results.length === 0) {
          setLinkError(
            'No place found for this link. Try the search box above or a link that includes coordinates.'
          );
          return;
        }
        setSearchResults([]);
        setMapSearchError('');
        await applyPlaceFromGeocode(results[0], {
          cityHintFromUrl: parsed.placePathCity,
          localityHintFromUrl: parsed.placePathLocality,
        });
        toast.success('Location applied from link (same as search).');
      } catch {
        setLinkError('Could not look up this place. Check your connection and try again.');
      } finally {
        setLinkApplying(false);
      }
      return;
    }

    const { lat: pLat, lng: pLng, placePathCity, placePathLocality } = parsed;
    const linkHints = {
      cityHintFromUrl: placePathCity,
      localityHintFromUrl: placePathLocality,
    };
    setLinkApplying(true);
    try {
      const rev = await reverseGeocodePhoton(pLat, pLng);
      if (rev) {
        await applyPlaceFromGeocode(rev, linkHints);
        toast.success('Address details filled from link location.');
      } else if (placePathCity) {
        await applyPlaceFromGeocode(
          {
            lat: pLat,
            lng: pLng,
            fullAddressLine: '',
            label: '',
            postcode: '',
            country: 'India',
          },
          linkHints
        );
        toast.info('Pin set. City and area taken from the link; add details if needed.');
      } else {
        setLat(String(pLat));
        setLng(String(pLng));
        toast.info('Pin set. Add address details manually if needed.');
      }
    } catch {
      setLat(String(pLat));
      setLng(String(pLng));
      toast.info('Pin set. Add address details manually if needed.');
    } finally {
      setLinkApplying(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!shop?._id) return;
    if (!markerValid) {
      return;
    }
    const address = {
      fullAddress: fullAddress.trim(),
      landmark: landmark.trim(),
      city: city || undefined,
      postalCode: postalCode.trim(),
      country: country.trim() || 'India',
      latitude: latNum,
      longitude: lngNum,
    };
    try {
      const res = await updateShopMutation.mutateAsync({
        id: shop._id,
        data: { address },
      });
      const updated = res?.data?.shop;
      if (updated && onSaved) onSaved(updated);
      onClose();
    } catch {
      /* toast from hook */
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Shop location</Modal.Header>
      <Modal.Body>
        <Typography variant="body2" style={{ color: '#64748b', marginBottom: 16 }}>
          Search for a place, set the pin on the map, paste a Google Maps link, or type coordinates.
          Saving updates this shop&apos;s address.
        </Typography>

        <div className={styles.locationModalSearchRow}>
          <div className={styles.locationModalSearchInput}>
            <Input.Root name="map-search">
              <Input.Label>Search map</Input.Label>
              <Input.Control
                value={mapSearchQuery}
                onChange={(e) => {
                  setMapSearchQuery(e.target.value);
                  setMapSearchError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runMapSearch();
                  }
                }}
                placeholder="Place name, area, or address (worldwide)"
              />
            </Input.Root>
          </div>
          <Button
            type="button"
            variant="primary"
            onClick={runMapSearch}
            disabled={mapSearchLoading}
            className={styles.locationModalSearchBtn}
          >
            <Search size={18} aria-hidden style={{ marginRight: 6 }} />
            {mapSearchLoading ? 'Searching…' : 'Search'}
          </Button>
        </div>
        {mapSearchError ? (
          <Typography
            variant="caption"
            style={{ color: '#dc2626', marginBottom: 8, display: 'block' }}
          >
            {mapSearchError}
          </Typography>
        ) : null}
        {searchResults.length > 0 ? (
          <ul className={styles.locationModalResultList} role="listbox" aria-label="Search results">
            {searchResults.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={styles.locationModalResultBtn}
                  onClick={() => pickSearchResult(r)}
                >
                  {r.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className={styles.locationModalMapWrap}>
          <MapContainer
            key={shop?._id || 'map'}
            center={center}
            zoom={markerValid ? 15 : 12}
            className={styles.locationModalMap}
            scrollWheelZoom
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapSizeFix />
            <MapRecenter lat={markerValid ? latNum : null} lng={markerValid ? lngNum : null} />
            <MapClickHandler onPick={handlePick} />
            {markerValid ? <Marker position={[latNum, lngNum]} /> : null}
          </MapContainer>
          <p className={styles.locationModalHint}>
            Click the map to place the pin. Coordinates update below.
          </p>
        </div>

        <div className={styles.locationModalRow}>
          <div className={styles.locationModalField}>
            <Input.Root name="loc-lat">
              <Input.Label>Latitude</Input.Label>
              <Input.Control
                type="number"
                step="any"
                value={lat}
                onChange={(e) => {
                  setLat(e.target.value);
                  setLinkError('');
                }}
                placeholder="e.g. 19.076"
              />
            </Input.Root>
          </div>
          <div className={styles.locationModalField}>
            <Input.Root name="loc-lng">
              <Input.Label>Longitude</Input.Label>
              <Input.Control
                type="number"
                step="any"
                value={lng}
                onChange={(e) => {
                  setLng(e.target.value);
                  setLinkError('');
                }}
                placeholder="e.g. 72.8777"
              />
            </Input.Root>
          </div>
        </div>

        <div className={styles.locationModalLinkBlock}>
          <Input.Root name="maps-link">
            <Input.Label>Paste Google Maps link</Input.Label>
            <Input.Control
              type="textarea"
              rows={3}
              value={mapsLink}
              onChange={(e) => {
                setMapsLink(e.target.value);
                setLinkError('');
              }}
              placeholder="Open Google Maps → Share → Copy link, and paste here (URL should contain @lat,lng or ?q=lat,lng)"
            />
          </Input.Root>
          {linkError ? (
            <Typography
              variant="caption"
              style={{ color: '#dc2626', marginTop: 8, display: 'block' }}
            >
              {linkError}
            </Typography>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            style={{ marginTop: 8 }}
            onClick={applyMapsLink}
            disabled={linkApplying}
          >
            {linkApplying ? 'Applying…' : 'Apply link to pin'}
          </Button>
        </div>

        <div className={styles.locationModalDivider} />

        <form onSubmit={handleSave}>
          <RowFields
            fullAddress={fullAddress}
            setFullAddress={setFullAddress}
            landmark={landmark}
            setLandmark={setLandmark}
            city={city}
            setCity={setCity}
            postalCode={postalCode}
            setPostalCode={setPostalCode}
            country={country}
            setCountry={setCountry}
            cityOptions={cityOptions}
          />
          <div className={styles.modalActions}>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={updateShopMutation.isPending || !markerValid}
            >
              {updateShopMutation.isPending ? 'Saving…' : 'Save location'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}

function RowFields({
  fullAddress,
  setFullAddress,
  landmark,
  setLandmark,
  city,
  setCity,
  postalCode,
  setPostalCode,
  country,
  setCountry,
  cityOptions,
}) {
  return (
    <>
      <Input.Root name="loc-full">
        <Input.Label>Street / full address</Input.Label>
        <Input.Control
          value={fullAddress}
          onChange={(e) => setFullAddress(e.target.value)}
          placeholder="Building, street, area"
        />
      </Input.Root>
      <div className={styles.locationModalRow} style={{ marginTop: 12 }}>
        <div className={styles.locationModalField}>
          <Input.Root name="loc-city">
            <Input.Label>City</Input.Label>
            <Input.Control
              type="select"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              options={[{ label: 'Select city', value: '' }, ...cityOptions]}
            />
          </Input.Root>
        </div>
        <div className={styles.locationModalField}>
          <Input.Root name="loc-landmark">
            <Input.Label>Landmark (optional)</Input.Label>
            <Input.Control value={landmark} onChange={(e) => setLandmark(e.target.value)} />
          </Input.Root>
        </div>
      </div>
      <div className={styles.locationModalRow}>
        <div className={styles.locationModalField}>
          <Input.Root name="loc-postal">
            <Input.Label>Postal code</Input.Label>
            <Input.Control value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
          </Input.Root>
        </div>
        <div className={styles.locationModalField}>
          <Input.Root name="loc-country">
            <Input.Label>Country</Input.Label>
            <Input.Control value={country} onChange={(e) => setCountry(e.target.value)} />
          </Input.Root>
        </div>
      </div>
    </>
  );
}
